import MarketingLayout from "@/components/MarketingLayout";
import { getBlogPostBySlug } from "@/lib/blogPosts";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { Streamdown } from "streamdown";
import { useLocation, useParams } from "wouter";

function formatPublishedDate(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(date));
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return (
      <MarketingLayout>
        <section className="container py-16">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold text-foreground mb-3">
              Blog post not found
            </h1>
            <p className="text-muted-foreground mb-6">
              The post may have moved or the link is incorrect.
            </p>
            <button
              type="button"
              onClick={() => setLocation("/blog")}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted/40"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </button>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section className="container py-16">
        <article className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => setLocation("/blog")}
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </button>

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="w-4 h-4" />
              <time dateTime={post.publishedAt}>
                {formatPublishedDate(post.publishedAt)}
              </time>
            </span>
            <span>{post.readingTime}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-8">{post.title}</h1>

          <div className="prose prose-neutral max-w-none dark:prose-invert prose-headings:scroll-mt-20">
            <Streamdown>{post.content.trimStart().replace(/^#\s+.+\n*/, "").trimStart()}</Streamdown>
          </div>
        </article>
      </section>
    </MarketingLayout>
  );
}
